import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from './context/AuthContext';
import { useLanguage } from './context/LanguageContext';
import { useCollection } from './hooks/useCollection';
import { supabase } from './config/supabase';
import { sendTelegramNotification } from './utils/telegram';
import { buildTelegramMessage } from './utils/telegramMessages';

// Layout and Authentication Components
import Sidebar from './features/shared/layout/Sidebar';
import LoginPage from './features/auth/components/LoginPage';
import ErrorBoundary from './features/shared/components/ErrorBoundary';
import { writeSettingsCache } from './services/settingsService';

// Default settings — used as the clean base when loading an agency's settings,
// so no keys from a previously logged-in agency can leak in.
const DEFAULT_SETTINGS = {
  companyName: 'CAR TRACK APP',
  notificationChannel: 'telegram',   // 'telegram' | 'push' | 'both'
  companyAddress: '',
  companyCity: '',
  companyPhone: '',
  companyEmail: '',
  companyICE: '',
  companyRC: '',
  companyIF: '',
  alertVidange: true,
  alertClientRetard: true,
  vidangeAlertWarning: 1000,
  vidangeAlertUrgent: 500,
  currency: 'MAD'
};

// Feature Pages
import Dashboard from './features/dashboard/components/Dashboard';
import ClientsPage from './features/clients/components/ClientsPage';
import CarsPage from './features/cars/components/CarsPage';
import CarInformationPage from './features/cars/components/CarInformationPage';
import RentalsPage from './features/rentals/components/RentalsPage';
import CalendarPage from './features/calendar/components/CalendarPage';
import VidangePage from './features/maintenance/components/VidangePage';
import ChequesPage from './features/cheques/components/ChequesPage';
import StaffPage from './features/staff/components/StaffPage';
import MokhalafatPage from './features/mokhalafat/components/MokhalafatPage';
import ReservationsPage from './features/reservations/components/ReservationsPage';
import InvoicesPage from './features/invoices/components/InvoicesPage';
import FinancesPage from './features/finances/components/FinancesPage';
import HistoryPage from './features/history/components/HistoryPage';
import GPSPage from './features/gps/components/GPSPage';
import SettingsPage from './features/settings/components/SettingsPage';
import BreakdownsPage from './features/breakdowns/components/BreakdownsPage';
import CarAnalyticsPage from './features/analytics/components/CarAnalyticsPage';
import ReferralPage from './features/referral/components/ReferralPage';
import BlacklistCheckPage from './features/blacklist/components/BlacklistCheckPage';
import PushNotificationsPage from './features/push/components/PushNotificationsPage';

// Special Access Pages
import SuperAdminApp from './features/superadmin/components/SuperAdminApp';
import SignaturePage from './features/rentals/components/SignaturePage';
import PublicReservationPage from './features/reservations/components/PublicReservationPage';

export default function App() {
  const { 
    user, 
    loading: authLoading, 
    agencyId, 
    signatureMode, 
    reserveMode, 
    reserveAgencyId, 
    superAdminMode 
  } = useAuth();
  
  const { t, lang } = useLanguage();

  const [currentPage, setCurrentPage] = useState('dashboard');
  const [carInfoPageCar, setCarInfoPageCar] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    if (saved !== null) return saved === 'true';
    return window.innerWidth >= 1024;
  });

  const [settings, setSettings] = useState(() => {
    // Start from defaults; the per-agency settings are loaded from the DB once
    // the agency is known (see effect below). We DON'T seed from the global cache
    // here to avoid briefly showing another agency's data on a shared device.
    return { ...DEFAULT_SETTINGS };
  });

  // Sync settings helper — writes the agency-scoped cache (never a shared key).
  const handleSaveSettings = (newSettings) => {
    writeSettingsCache(newSettings, agencyId);
    setSettings(newSettings);
  };

  const saveAndSyncSettings = async (newSettings) => {
    writeSettingsCache(newSettings, agencyId);
    setSettings(newSettings);

    if (user) {
      try {
        const userId = user.id || user.uid;
        const { data: reg } = await supabase
          .from('user_registrations')
          .select('data')
          .eq('uid', userId)
          .single();

        const currentData = reg?.data || {};
        const mergedData = { ...currentData, ...newSettings };

        await supabase
          .from('user_registrations')
          .update({ data: mergedData })
          .eq('uid', userId); // scoped to THIS agency's row only
      } catch (err) {
        console.error('Failed to sync settings to Supabase:', err.message);
      }
    }
  };

  const handleDismissAlert = async (alertId) => {
    if (window.confirm(lang === 'ar' ? 'هل تريد إخفاء هذا التنبيه؟' : 'Voulez-vous masquer cette alerte ?')) {
      const dismissed = [...(settings.dismissedAlerts || []), alertId];
      await saveAndSyncSettings({ ...settings, dismissedAlerts: dismissed });
    }
  };

  // Load settings from Supabase on startup / login
  useEffect(() => {
    if (!user) {
      setSettings({ ...DEFAULT_SETTINGS });
      return;
    }
    const loadSettingsFromDb = async () => {
      try {
        const userId = user.id || user.uid;
        if (!userId) return;

        const { data: reg, error } = await supabase
          .from('user_registrations')
          .select('data')
          .eq('uid', userId)
          .single();

        if (reg && reg.data) {
          // REPLACE over a clean default base (not over the previous agency's
          // state) so no settings from another agency can leak into this one.
          const merged = { ...DEFAULT_SETTINGS, ...reg.data };
          writeSettingsCache(merged, reg.data.agencyId || userId);
          setSettings(merged);
        } else {
          // No row yet → start from clean defaults for this agency.
          setSettings({ ...DEFAULT_SETTINGS });
        }
      } catch (err) {
        console.error('Failed to load settings from Supabase:', err.message);
      }
    };

    loadSettingsFromDb();
  }, [user]);

  // Presence heartbeat — mark this agency "online" by writing data.lastActiveAt
  // every 60s. Used by the Super Admin "Online Agencies Now" metric. Skipped for
  // super-admin and impersonation sessions so they don't fake an agency online.
  useEffect(() => {
    if (!user || superAdminMode) return;
    const uid = user.id || user.uid;
    if (!uid) return;
    const isImpersonation = !!new URLSearchParams(window.location.search).get('impersonate');
    if (isImpersonation) return;

    let stopped = false;
    const beat = async () => {
      try {
        const { data: reg } = await supabase
          .from('user_registrations')
          .select('data')
          .eq('uid', uid)
          .maybeSingle();
        const merged = { ...(reg?.data || {}), lastActiveAt: new Date().toISOString() };
        await supabase.from('user_registrations').update({ data: merged }).eq('uid', uid);
      } catch (_) { /* presence is best-effort */ }
    };
    beat(); // immediate on login
    const id = setInterval(() => { if (!stopped) beat(); }, 60 * 1000);
    return () => { stopped = true; clearInterval(id); };
  }, [user, superAdminMode]);

  // Real-time Global Collections
  const { data: globalCars, update: updateCar, loading: carsLoading } = useCollection('cars');
  const { data: globalRentals, loading: rentalsLoading } = useCollection('rentals');
  const { data: globalVidanges } = useCollection('vidanges');
  const { data: globalClients } = useCollection('clients');
  const { data: globalCheques } = useCollection('cheques');
  const { data: globalStaff } = useCollection('staff');
  const { data: globalStaffPayments } = useCollection('staff_payments');

  // Database Consistency Check: auto-heal orphaned or desynchronized car statuses
  const lastCheckedAgencyId = useRef(null);
  useEffect(() => {
    if (carsLoading || rentalsLoading || !globalCars.length || !globalRentals.length || !agencyId) return;
    if (lastCheckedAgencyId.current === agencyId) return;
    lastCheckedAgencyId.current = agencyId;

    const checkAndFixStatuses = async () => {
      const activeCarIds = new Set(
        globalRentals
          .filter(r => r.status === 'active')
          .map(r => r.carId)
          .filter(Boolean)
      );

      // Find cars currently set to 'rented' but with no active rentals
      const stuckCars = globalCars.filter(
        c => c.status === 'rented' && !activeCarIds.has(c.id)
      );

      // Find cars set to 'available' but with active rentals
      const activeButAvailableCars = globalCars.filter(
        c => c.status === 'available' && activeCarIds.has(c.id)
      );

      for (const car of stuckCars) {
        try {
          await updateCar(car.id, { status: 'available' });
          console.log(`[Consistency Check] Restored car status to available: ${car.brand} ${car.model} (${car.plate})`);
        } catch (err) {
          console.error(`[Consistency Check] Failed to restore car ${car.id}:`, err.message);
        }
      }

      for (const car of activeButAvailableCars) {
        try {
          await updateCar(car.id, { status: 'rented' });
          console.log(`[Consistency Check] Marked car status as rented: ${car.brand} ${car.model} (${car.plate})`);
        } catch (err) {
          console.error(`[Consistency Check] Failed to set car ${car.id} to rented:`, err.message);
        }
      }
    };

    checkAndFixStatuses();
  }, [carsLoading, rentalsLoading, globalCars, globalRentals, updateCar, agencyId]);

  // Compute Active Alerts
  const activeAlerts = useMemo(() => {
    const list = [];
    if (!agencyId) return list;

    const todayStr = new Date().toISOString().split('T')[0];

    // 1. Comprehensive Maintenance & Oil Changes Alerts
    const maintWarning = settings.maintWarningThreshold ?? 80;
    const maintCritical = settings.maintCriticalThreshold ?? 90;
    const maintOverdue = settings.maintOverdueThreshold ?? 100;

    const MAINTENANCE_TYPES = [
      { key: 'Vidange', fr: 'Vidange d\'huile', ar: 'تغيير الزيت' },
      { key: 'Filtre à huile', fr: 'Filtre à huile', ar: 'فلتر الزيت' },
      { key: 'Filtre à air', fr: 'Filtre à air', ar: 'فلتر الهواء' },
      { key: 'Filtre à carburant', fr: 'Filtre à carburant', ar: 'فلتر الوقود' },
      { key: 'Courroie de distribution', fr: 'Courroie de distribution', ar: 'حزام التوقيت' },
      { key: 'Pneus', fr: 'Pneus', ar: 'الإطارات' },
      { key: 'Freins', fr: 'Freins', ar: 'الفرامل' },
      { key: 'Bougies', fr: 'Bougies', ar: 'الشمعات' },
      { key: 'Batterie', fr: 'Batterie', ar: 'البطارية' },
      { key: 'Climatisation', fr: 'Climatisation', ar: 'التكييف' },
      { key: 'Autre', fr: 'Autre', ar: 'أخرى' }
    ];

    const getDefaultInterval = (type) => {
      const intervals = settings?.maintIntervals || {};
      if (intervals[type] !== undefined) return parseFloat(intervals[type]) || 10000;
      switch(type) {
        case 'Vidange':
        case 'Filtre à huile': return 10000;
        case 'Filtre à air':
        case 'Climatisation': return 20000;
        case 'Filtre à carburant':
        case 'Freins':
        case 'Bougies': return 30000;
        case 'Pneus': return 40000;
        case 'Batterie': return 60000;
        case 'Courroie de distribution': return 80000;
        default: return 10000;
      }
    };

    globalCars.forEach(car => {
      const effectiveKm = (parseFloat(car.km) || 0) + (parseFloat(car.gpsOdometer) || 0);

      MAINTENANCE_TYPES.forEach(item => {
        const carMaint = globalVidanges.filter(v => (v.carId === car.id || v.carId === car._id) && v.type === item.key);
        carMaint.sort((a, b) => (parseFloat(b.currentKm) || 0) - (parseFloat(a.currentKm) || 0));
        const latest = carMaint[0] || null;

        let lastKm = 0;
        let nextKm = 0;
        if (latest) {
          lastKm = parseFloat(latest.currentKm) || 0;
          nextKm = parseFloat(latest.nextKm) || 0;
        } else {
          lastKm = 0;
          nextKm = getDefaultInterval(item.key);
        }

        const interval = nextKm - lastKm;
        const driven = Math.max(0, effectiveKm - lastKm);
        const remaining = nextKm - effectiveKm;
        const pct = interval > 0 ? Math.round((driven / interval) * 100) : 0;

        let shouldAlert = false;
        let severity = 'orange';
        let alertLevel = '';

        if (pct >= maintOverdue) {
          shouldAlert = true;
          severity = 'red';
          alertLevel = 'overdue';
        } else if (pct >= maintCritical) {
          shouldAlert = true;
          severity = 'red';
          alertLevel = 'critical';
        } else if (pct >= maintWarning) {
          shouldAlert = true;
          severity = 'orange';
          alertLevel = 'warning';
        }

        if (shouldAlert) {
          const typeName = lang === 'ar' ? item.ar : item.fr;
          const title = lang === 'ar'
            ? `${typeName} - ${car.brand} ${car.model} (${car.plate})`
            : `${typeName} - ${car.brand} ${car.model} (${car.plate})`;

          let message = '';
          if (lang === 'ar') {
            if (alertLevel === 'overdue') {
              message = `تجاوز مسافة الخدمة بـ ${Math.round(Math.abs(remaining)).toLocaleString()} كم! (الحالي: ${Math.round(effectiveKm).toLocaleString()} كم، المتوقع: ${Math.round(nextKm).toLocaleString()} كم)`;
            } else {
              message = `متبقي ${Math.round(remaining).toLocaleString()} كم على الخدمة. (الحالي: ${Math.round(effectiveKm).toLocaleString()} كم، المتوقع: ${Math.round(nextKm).toLocaleString()} كم، نسبة الإنجاز: ${pct}%)`;
            }
          } else {
            if (alertLevel === 'overdue') {
              message = `Maintenance dépassée de ${Math.round(Math.abs(remaining)).toLocaleString()} km ! (Actuel: ${Math.round(effectiveKm).toLocaleString()} km, Échéance: ${Math.round(nextKm).toLocaleString()} km)`;
            } else {
              message = `Il reste ${Math.round(remaining).toLocaleString()} km avant échéance. (Actuel: ${Math.round(effectiveKm).toLocaleString()} km, Échéance: ${Math.round(nextKm).toLocaleString()} km, Progression: ${pct}%)`;
            }
          }

          list.push({
            id: `maintenance-${car.id}-${item.key}-${alertLevel}`,
            type: 'vidange', // Wrench icon on Dashboard
            maintType: item.key,
            maintTypeName: typeName,
            vidangeLevel: alertLevel,
            severity,
            carId: car.id,
            carName: `${car.brand} ${car.model}`,
            plate: car.plate,
            kmRemaining: remaining,
            currentKm: effectiveKm,
            nextVidangeKm: nextKm,
            pct,
            title,
            message
          });
        }
      });
    });

    // 2. Overdue Client
    if (settings.alertClientRetard !== false) {
      globalRentals.forEach(rental => {
        if (rental.status === 'active' && rental.endDate && rental.endDate < todayStr) {
          const client = globalClients.find(c => c.id === rental.clientId || c._id === rental.clientId);
          
          const title = lang === 'ar'
            ? `تأخر في إرجاع السيارة - ${rental.carName}`
            : `Retard de restitution - ${rental.carName}`;
          const message = lang === 'ar'
            ? `العميل ${rental.clientName} كان يجب أن يرجع السيارة في ${rental.endDate}.`
            : `Le client ${rental.clientName} devait restituer le véhicule le ${rental.endDate}.`;

          list.push({
            id: `retard-${rental.id}`,
            type: 'retard',
            clientName: rental.clientName,
            clientPhone: client?.phone || '',
            carName: rental.carName,
            severity: 'red',
            rentalId: rental.id,
            endDate: rental.endDate,
            title,
            message
          });
        }
      });
    }

    const filtered = list.filter(alert => !(settings.dismissedAlerts || []).includes(alert.id));
    return filtered;
  }, [globalCars, globalVidanges, globalRentals, globalClients, settings, agencyId, lang]);

  // 3. Telegram Maintenance Alerts Handler
  useEffect(() => {
    if (!user || !settings.telegramChatId || !activeAlerts.length) return;

    let notifiedAlerts = {};
    try {
      const saved = localStorage.getItem('maint_notified_alerts');
      if (saved) notifiedAlerts = JSON.parse(saved);
    } catch (_) {}

    const todayStr = new Date().toISOString().split('T')[0];
    let changed = false;

    activeAlerts.forEach(alert => {
      if (!alert.id.startsWith('maintenance-')) return;

      const alertKey = `${alert.carId}-${alert.maintType}-${alert.vidangeLevel}`;
      const notifType = `maint_${alert.vidangeLevel}`; // maint_warning, maint_critical, maint_overdue
      const isTelegramEnabled = settings.telegramNotifications?.[notifType] !== false;

      if (isTelegramEnabled && !notifiedAlerts[alertKey]) {
        const msg = buildTelegramMessage(notifType, {
          typeKey: alert.maintType, carName: alert.carName, plate: alert.plate,
          level: alert.vidangeLevel, currentKm: alert.currentKm, nextKm: alert.nextVidangeKm
        });

        sendTelegramNotification(msg, notifType);
        notifiedAlerts[alertKey] = todayStr;
        changed = true;
      }
    });

    // Reset notification states for resolved alerts
    const activeAlertKeys = new Set(
      activeAlerts
        .filter(a => a.id.startsWith('maintenance-'))
        .map(a => `${a.carId}-${a.maintType}`)
    );
    
    Object.keys(notifiedAlerts).forEach(key => {
      const parts = key.split('-');
      if (parts.length >= 2) {
        const carId = parts[0];
        const maintType = parts[1];
        if (!activeAlertKeys.has(`${carId}-${maintType}`)) {
          delete notifiedAlerts[key];
          changed = true;
        }
      }
    });

    if (changed) {
      localStorage.setItem('maint_notified_alerts', JSON.stringify(notifiedAlerts));
    }
  }, [activeAlerts, settings, user, lang]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // Route routing fallbacks
  if (signatureMode) {
    return <SignaturePage rentalId={signatureMode} settings={settings} />;
  }

  if (reserveMode) {
    return <PublicReservationPage settings={settings} agencyId={reserveAgencyId} />;
  }

  if (superAdminMode) {
    return <SuperAdminApp />;
  }

  if (!user) {
    return <LoginPage settings={settings} />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return (
          <Dashboard 
            settings={settings} 
            activeAlerts={activeAlerts} 
            setCurrentPage={setCurrentPage} 
            globalCars={globalCars}
            globalClients={globalClients}
            globalRentals={globalRentals}
            globalVidanges={globalVidanges}
            onDismissAlert={handleDismissAlert}
          />
        );
      case 'clients':
        return <ClientsPage />;
      case 'cars':
        return <CarsPage settings={settings} onOpenInfo={(car) => { setCarInfoPageCar(car); setCurrentPage('car-info'); }} />;
      case 'car-info':
        return (
          <CarInformationPage
            car={carInfoPageCar}
            settings={settings}
            onBack={() => { setCarInfoPageCar(null); setCurrentPage('cars'); }}
          />
        );
      case 'rentals':
        return <RentalsPage settings={settings} />;
      case 'calendar':
        return <CalendarPage />;
      case 'vidange':
        return <VidangePage settings={settings} />;
      case 'cheques':
        return <ChequesPage settings={settings} />;
      case 'staff':
        return <StaffPage settings={settings} />;
      case 'mokhalafat':
        return <MokhalafatPage settings={settings} />;
      case 'reservations':
        return <ReservationsPage settings={settings} />;
      case 'invoices':
        return <InvoicesPage settings={settings} />;
      case 'finances':
        return <FinancesPage settings={settings} />;
      case 'history':
        return <HistoryPage settings={settings} />;
      case 'gps':
        return <GPSPage settings={settings} />;
      case 'breakdowns':
        return <BreakdownsPage settings={settings} />;
      case 'analytics':
        return <CarAnalyticsPage settings={settings} />;
      case 'referral':
        return <ReferralPage settings={settings} />;
      case 'blacklistcheck':
        return <BlacklistCheckPage settings={settings} />;
      case 'push':
        return <PushNotificationsPage />;
      case 'settings':
        return (
          <SettingsPage 
            settings={settings} 
            setSettings={handleSaveSettings} 
          />
        );
      default:
        return <div className="p-4">Page non trouvée.</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 transition-colors duration-200" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <Sidebar 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage} 
        settings={settings} 
        alertsCount={activeAlerts.length}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />
      <main className={`p-3 sm:p-4 lg:p-8 pt-20 lg:pt-8 transition-all duration-300 ${sidebarOpen ? 'lg:ms-64' : 'lg:ms-0'}`}>
        <ErrorBoundary resetKey={`${currentPage}|${carsLoading}|${rentalsLoading}`} lang={lang}>
          {renderPage()}
        </ErrorBoundary>
      </main>
    </div>
  );
}
